import { useEffect, useRef } from 'react';
import { useThumbnailQueue } from './useThumbnailQueue';

interface Document {
  id: string;
  file_path: string;
  user_id: string;
  mime_type: string | null;
  thumbnail_path: string | null;
}

/**
 * Background hook that detects PDFs without thumbnails and queues them for generation.
 * Works silently in the background - no UI, no user interaction needed.
 * 
 * @param documents - Array of documents to scan for missing thumbnails
 */
export function useBackgroundThumbnails(documents: Document[]) {
  const { addToQueue } = useThumbnailQueue();
  const processedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Filter for PDFs without thumbnail that haven't been queued yet
    const pdfsToProcess = documents.filter(doc =>
      doc.mime_type?.includes('pdf') &&
      !doc.thumbnail_path &&
      !processedRef.current.has(doc.id)
    );

    if (pdfsToProcess.length === 0) return;

    // Add each to queue and mark as processed
    pdfsToProcess.forEach(doc => {
      processedRef.current.add(doc.id);
      addToQueue({
        id: doc.id,
        file_path: doc.file_path,
        user_id: doc.user_id
      });
    });

    // Log for dev visibility
    console.log(`[BackgroundThumbnails] Queued ${pdfsToProcess.length} PDFs for thumbnail generation`);
  }, [documents, addToQueue]);
}
